/**
 * Indicador de sincronización offline: muestra el estado de conexión, cuántas
 * operaciones quedan por sincronizar y los conflictos, con un botón para
 * sincronizar ahora. Solo aparece cuando hay algo que mostrar (offline, cola
 * pendiente o conflictos) y solo si la capa offline está activa (VITE_OFFLINE=1).
 */

import { useEffect, useState, useCallback } from "react";
import { Badge, Button } from "@fluentui/react-components";
import { useAuth } from "@core/auth/useAuth";
import { contarPendientes, enConflicto } from "@core/offline/outbox";
import { estaOnline, onConnectivityChange } from "@core/offline/connectivity";
import { sincronizar, onSyncChange } from "@core/offline/SyncEngine";

const OFFLINE = import.meta.env.VITE_OFFLINE === "1";

export function SyncStatus(): JSX.Element | null {
  const { user } = useAuth();
  const tenantId = user?.tenantId ?? "";
  const [pendientes, setPendientes] = useState(0);
  const [conflictos, setConflictos] = useState(0);
  const [online, setOnline] = useState(estaOnline());

  const refrescar = useCallback(async () => {
    if (!tenantId) return;
    setPendientes(await contarPendientes(tenantId));
    setConflictos((await enConflicto(tenantId)).length);
    setOnline(estaOnline());
  }, [tenantId]);

  useEffect(() => {
    if (!OFFLINE || !tenantId) return;
    void refrescar();
    const offSync = onSyncChange(() => void refrescar());
    const offConn = onConnectivityChange(() => void refrescar());
    return () => {
      offSync();
      offConn();
    };
  }, [tenantId, refrescar]);

  if (!OFFLINE || !tenantId) return null;
  // Todo sincronizado y con conexión → no estorbar.
  if (online && pendientes === 0 && conflictos === 0) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {!online && (
        <Badge appearance="tint" color="warning">
          Sin conexión
        </Badge>
      )}
      {pendientes > 0 && (
        <Badge appearance="tint" color="informative">
          {pendientes} por sincronizar
        </Badge>
      )}
      {conflictos > 0 && (
        <Badge appearance="tint" color="danger">
          {conflictos} en conflicto
        </Badge>
      )}
      {online && pendientes > 0 && (
        <Button size="small" appearance="primary" onClick={() => void sincronizar(tenantId)}>
          Sincronizar ahora
        </Button>
      )}
    </div>
  );
}
